import {
	boolean,
	equal,
	integer,
	required,
	min,
	string,
	length,
	rule,
	messages,
} from 'vue-mc/validation';
import moment from 'moment';
import dateValidator from './validators/date';
import skipValidation from './validators/skip';
import Model from './Model';
import Collection from './Collection';

/**
 * Custom validators
 */
const nameAndDateNotEmpty = rule({
	name: 'nameAndDateNotEmpty',
	test: (value, propName, report) => {
		return !(report.name.trim() === '' && !report.date);
	},
});

/**
 * Project model
 *
 * @param {number} id
 * @param {string} name
 * @param {boolean} isArchived
 */
export class Report extends Model {

	// Default attributes that define the "empty" state.
	defaults() {
		return {
			id: null,
			projectId: null,
			name: '',
			description: '',
			date: moment().format('YYYY-MM-DD'),
			isArchived: false,
			image: null,
			images: [],
			lastActive: null,
			stats: {
				activeArticlesCount: 0,
			},
		};
	}

	// Attribute mutations.
	mutations() {
		return {
			id: id => Number(id) || null,
			projectId: id => Number(id) || null,
			name: String,
			description: String,
			isArchived: Boolean,
		};
	}

	// Attribute validation
	validation() {
		return {
			id: integer.and(min(1)).or(equal(null)),
			projectId: required.and(integer),
			isArchived: boolean,
			name: skipValidation.or(
				string.and(length(0, 50)).and(nameAndDateNotEmpty),
			),
			date: skipValidation.or(
				dateValidator.and(nameAndDateNotEmpty),
			),
		};
	}

	// Route configuration
	routes() {
		return {
			create: '/reports',
			fetch: '/reports/{id}',
			patch: '/reports/{id}',
			delete: '/reports/{id}',
			uploadImage: 'reports/{id}/upload-image',
			copyArticles: 'reports/{id}/batch-copy',
			moveArticles: 'reports/{id}/batch-move',
			deleteArticles: 'reports/{id}/batch-delete',
			refetchArticlesStats: 'reports/{id}/batch-refetch-url-data',
		};
	}

	options() {
		return {
			validateOnChange: true,
			patch: true,
			methods: {
				uploadImage: 'POST',
				copyArticles: 'POST',
				moveArticles: 'POST',
				deleteArticles: 'POST',
				refetchArticlesStats: 'POST',
			},
		};
	}

	uploadImage(uri) {
		const method = this.getOption('methods.uploadImage');
		const route = this.getRoute('uploadImage');
		const url = this.getURL(route, this.getRouteParameters());

		return new Promise((resolve, reject) => {
			this.loading = true;
			this.getRequest({ method,
				url,
				headers: { Authorization: '' },
				data: { uri },
			})
				.send()
				.then(({ response }) => {
					this.image = response.data.image;
					this.images = response.data.images;
					resolve(response);
				}).catch((error) => {
					reject(error);
				})
				.finally(() => {
					this.loading = false;
				});
		});
	}

	copyArticles(articleIds, categoryId) {
		const method = this.getOption('methods.copyArticles');
		const route = this.getRoute('copyArticles');
		const url = this.getURL(route, this.getRouteParameters());
		const data = { articleIds, attributes: { categoryId } };

		return new Promise((resolve, reject) => {
			this.getRequest({ method, url, data, headers: { Authorization: '' } })
				.send()
				.then(() => {
					resolve();
				})
				.catch((error) => {
					reject(error);
				});
		});
	}

	moveArticles(articleIds, categoryId) {
		const method = this.getOption('methods.moveArticles');
		const route = this.getRoute('moveArticles');
		const url = this.getURL(route, this.getRouteParameters());
		const data = { articleIds, attributes: { categoryId } };

		return new Promise((resolve, reject) => {
			this.getRequest({ method, url, data, headers: { Authorization: '' } })
				.send()
				.then(() => {
					resolve();
				})
				.catch((error) => {
					reject(error);
				});
		});
	}

	deleteArticles(articleIds) {
		const method = this.getOption('methods.deleteArticles');
		const route = this.getRoute('deleteArticles');
		const url = this.getURL(route, this.getRouteParameters());
		const data = { articleIds };

		return new Promise((resolve, reject) => {
			this.getRequest({ method, url, data, headers: { Authorization: '' } })
				.send()
				.then(() => {
					resolve();
				})
				.catch((error) => {
					reject(error);
				});
		});
	}
	
	refetchArticlesStats(articleIds) {
		const method = this.getOption('methods.refetchArticlesStats');
		const route = this.getRoute('refetchArticlesStats');
		const url = this.getURL(route, this.getRouteParameters());
		const data = { articleIds };

		return new Promise((resolve, reject) => {
			this.getRequest({ method, url, data, headers: { Authorization: '' } })
				.send()
				.then(() => {
					resolve();
				})
				.catch((error) => {
					reject(error);
				});
		});
	}

	get displayName() {
		if (this.name) {
			return this.name;
		}
		if (this.date) {
			return this.displayDate;
		}
		return '';
	}

	get displayDate() {
		if (!this.date) {
			return '';
		}
		const a = this.date.split('-');
		const d = new Date(a[0], a[1] - 1, a[2]);
		return d.toLocaleDateString("en-US", { year: 'numeric', month: 'long', day: 'numeric' });
	}
	
	get url() {
		return {
			name: 'ArticlesList',
			params: {
				reportId: this.id,
			},
		};
	}

	/**
	 * @returns {Project} Related project model
	 */
	get projectModel() {
		return this.relation('projects/all', project => project.id === this.projectId);
	}
	
	/**
	 * @returns {boolean}
	 */
	get isArchivedOrParentArchived() {
		return this.isArchived || this.projectModel.isArchived;
	}

}

/**
 * Reports collection
 */
export class Reports extends Collection {

	model() {
		return Report;
	}

	routes() {
		return {
			fetch: '/reports?projectId={id}',
		};
	}

}

export class ActiveReports extends Reports {

	routes() {
		return {
			fetch: '/reports?projectId={id}&archived=0',
		};
	}

}

export class ArchivedReports extends Reports {

	routes() {
		return {
			fetch: '/reports?projectId={id}&archived=1',
		};
	}

}

/**
 * Recent Reports collection
 */
export class RecentReports extends Reports {

	routes() {
		return {
			fetch: '/reports',
		};
	}

}

messages.set('nameAndDateNotEmpty', 'Date and name cannot be empty');
